// 📁 src/pages/Cups.js
import React from "react";
import { Link } from "react-router-dom";
import Seo from "../components/seo";
import cardData from "../components/card-data";
import { cardPath } from "../utils/cardRoutes";
import { COMMON_COPY_ES, isSpanishLocale, localizedPath, translateCardName, translateKeywordPhrase, translateSuitName } from "../utils/i18n";

/* =========================================================
   THEME TOKENS (match MajorArcana.js)
========================================================= */
const WRAP = "px-[1vw]";
const MAXW = "max-w-10xl mx-auto";
const FONT = "font-[Cormorant_Garamond]";
const TEXT = "text-white/90";
const BORDER_SOFT = "border-[#005C83]/12";
const PANEL_BASE =
  "relative overflow-hidden rounded-md " +
  BORDER_SOFT +
  " shadow-[0_2px_4px_rgba(0,0,0,0.35)]";

/* Canvas background */
const CANVAS_BG = `
  radial-gradient(1200px 900px at 50% -12%, rgba(3,121,155,0.32), rgba(3,121,155,0) 40%),
  linear-gradient(180deg, #014C75 0%, #005C83 22%, #016280ff 38%, #0C4B84 72%, #014C75 90%, #821E3C 100%)
`;

/* Silk panel */
const SILK_TURQ =
  "bg-[linear-gradient(135deg,rgba(12,63,107,0.4),rgba(20,33,61,0.88))] " +
  "before:absolute before:inset-0 before:bg-[radial-gradient(80%_60%_at_80%_0%,rgba(59,198,214,0.25),transparent_100%)] before:content-['']";

/* =========================================================
   SEO
========================================================= */
const siteUrl = "https://tarotfate.com";
const pageUrl = `${siteUrl}/tarot/cups`; // matches App.js
const ogImage = `${siteUrl}/icons/og-cover.jpg`;   // 1200x630 social image
const logoUrl = `${siteUrl}/icons/logo.webp`;      // Organization logo (512x512)

const metaDescription =
  "Explore the Cups suit of tarot cards. Learn upright and reversed meanings for all 14 Cups cards-from the Ace to the King-covering love, emotions, intuition and healing in everyday readings.";

  const keywords = [
  "Cups tarot",
  "Cups suit meanings",
  "water element tarot",
  "emotions in tarot",
  "relationships tarot",
  "Ace of Cups meaning",
  "Two of Cups meaning",
  "Queen of Cups meaning",
  "Minor Arcana Cups",
  "upright reversed Cups"
].join(", ");

  
/* =========================================================
   Data: exact 14 slugs for Cups
========================================================= */
const cupsSlugs = [
  "ace-of-cups-tarot-card-reading-meaning",
  "two-of-cups-tarot-card-reading-meaning",
  "three-of-cups-tarot-card-reading-meaning",
  "four-of-cups-tarot-card-reading-meaning",
  "five-of-cups-tarot-card-reading-meaning",
  "six-of-cups-tarot-card-reading-meaning",
  "seven-of-cups-tarot-card-reading-meaning",
  "eight-of-cups-tarot-card-reading-meaning",
  "nine-of-cups-tarot-card-reading-meaning",
  "ten-of-cups-tarot-card-reading-meaning",
  "page-of-cups-tarot-card-reading-meaning",
  "knight-of-cups-tarot-card-reading-meaning",
  "queen-of-cups-tarot-card-reading-meaning",
  "king-of-cups-tarot-card-reading-meaning",
];

const cupsCards = Object.values(cardData).filter((c) => cupsSlugs.includes(c.slug));

/* =========================================================
   JSON-LD (CollectionPage + ItemList + FAQPage)
========================================================= */
function schemaGraphFor(items, faqEntities) {
  const itemListElement = items.map((c, i) => ({
    "@type": "ListItem",
    position: i + 1,
    name: c.name,
    url: `${siteUrl}${cardPath(c)}`,
  }));

  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${siteUrl}#org`,
        name: "TarotFate",
        url: siteUrl,
        logo: { "@type": "ImageObject", url: logoUrl, width: 512, height: 512 },
      },
      {
        "@type": "WebSite",
        "@id": `${siteUrl}#website`,
        url: siteUrl,
        name: "TarotFate",
        publisher: { "@id": `${siteUrl}#org` },
        inLanguage: "en",
      },
      {
        "@type": "CollectionPage",
        "@id": `${pageUrl}#webpage`,
        url: pageUrl,
        name: "Cups Tarot Cards | Meanings and Symbols | TarotFate",
        description: metaDescription,
        isPartOf: { "@id": `${siteUrl}#website` },
        primaryImageOfPage: { "@id": `${pageUrl}#image` },
        breadcrumb: { "@id": `${pageUrl}#breadcrumbs` },
        inLanguage: "en",
      },
      {
        "@type": "ImageObject",
        "@id": `${pageUrl}#image`,
        url: ogImage,
        width: 1200,
        height: 630,
        caption: "TarotFate cover",
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: siteUrl },
          { "@type": "ListItem", position: 2, name: "Tarot Cards", item: `${siteUrl}/tarot-cards` },
          { "@type": "ListItem", position: 3, name: "Cups", item: pageUrl },
        ],
      },
      {
        "@type": "ItemList",
        "@id": `${pageUrl}#cups`,
        name: "Cups",
        itemListElement,
      },
      {
        "@type": "FAQPage",
        "@id": `${pageUrl}#faq`,
        mainEntity: faqEntities,
      },
    ],
  };
}

/* =========================================================
   Copy blocks with no dashes in visible text
========================================================= */
const suitIntro =
  "The Cups suit represents the flow of emotions and the wisdom of the heart. Aligned with water, it explores love, relationships, intuition and healing. From the Ace's raw sensitivity to the King's emotional wisdom, Cups highlight romantic bonds, family, self-compassion and spiritual connection. The suit encourages honest communication, listening and healthy boundaries. When prominent, it signals heightened sensitivity and the need for care and mutual respect in choices. Cups can also reveal confusion or escapism, inviting grounding practices. The lesson of Cups is that emotion, when respected and channeled, leads to deeper connections and a more compassionate life.";

const heroExplainer = "";

export default function Cups() {
  const isSpanish = isSpanishLocale();
  // Rich FAQ for on page copy and JSON LD
  const faq = isSpanish
    ? [
        {
          q: "Que representan las Copas en el tarot?",
          a: "Copas representa el elemento agua. Habla de emociones, intuicion, relaciones, sanacion y empatia. En una lectura muestra sentimientos, vinculos y la calidad del cuidado en una situacion.",
        },
        {
          q: "Cuantas cartas de Copas hay en una baraja?",
          a: "Hay catorce cartas de Copas, desde el As hasta el Rey. Incluye diez cartas numeradas y cuatro cartas de la corte: Sota, Caballero, Reina y Rey.",
        },
        {
          q: "Que significa que aparezcan muchas Copas juntas?",
          a: "Muchas Copas enfocan la lectura en temas emocionales: confianza, familia, reconciliacion, ternura, limites sanos o autocuidado.",
        },
        {
          q: "Copas solo habla de romance?",
          a: "No. Copas tambien habla de amistad, familia, comunidad y de tu relacion contigo. Explora como la compasion y la honestidad cambian una situacion.",
        },
        {
          q: "Como trabajo la intuicion con Copas?",
          a: "Observa primero las sensaciones del cuerpo y las imagenes que se repiten. Luego lee los simbolos de la carta y une intuicion con una interpretacion clara.",
        },
        {
          q: "Debo usar significados invertidos con Copas?",
          a: "Usalos si aportan matiz. Si prefieres, lee al derecho y usa la posicion o la pregunta para dar contexto. Elige un metodo y aplicalo con consistencia.",
        },
      ]
    : [
        {
          q: "What do Cups represent in tarot?",
          a: "Cups represent the element of water. They describe emotion, intuition, relationship, healing and empathy. In a reading they highlight feelings, connection and the quality of care in a situation.",
        },
        {
          q: "How many Cups cards are in a deck?",
          a: "There are fourteen Cups cards from Ace through King. The suit includes ten numbered cards and four court cards called Page, Knight, Queen and King.",
        },
        {
          q: "What does it mean when many Cups appear together?",
          a: "Many Cups in one spread focus attention on emotional themes. This can include bonding, trust, family matters, reconciliation or the need for healthy boundaries and self care.",
        },
        {
          q: "Are Cups only about romance?",
          a: "No. Cups address many forms of relationship including friendship, family, community and your relationship with self. They explore how compassion and honesty shape outcomes.",
        },
        {
          q: "How can I work with intuition in the Cups suit?",
          a: "Notice body signals and recurring images first, then read the card page for symbols and meanings. Combine both sources to form a clear and grounded interpretation.",
        },
        {
          q: "Should I use reversed meanings for Cups?",
          a: "Use reversals if they add helpful nuance. If you prefer, read upright meanings and rely on position and question for context. Choose one approach and apply it with consistency.",
        },
      ];

  const faqEntities = faq.map(({ q, a }) => ({
    "@type": "Question",
    name: q,
    acceptedAnswer: { "@type": "Answer", text: a },
  }));

  const schemaGraph = schemaGraphFor(cupsCards, faqEntities);
  const title = isSpanish ? "Copas en el Tarot | Significados y Símbolos | TarotFate" : "Cups Tarot Cards | Meanings and Symbols | TarotFate";
  const intro = isSpanish
    ? "Copas representa emoción, intuición, amor, vínculos y sanación. Este palo ayuda a comprender lo que el corazón necesita, cómo se cuida una relación y dónde una emoción pide escucha."
    : suitIntro;

  return (
    <div
      className={`${FONT} text-white min-h-screen relative overflow-hidden`}
      style={{ background: CANVAS_BG }}
    >
      <Seo
  title={title}
  description={isSpanish ? "Explora Copas en el tarot con imágenes, símbolos y significados claros para amor, emociones, intuición y sanación." : metaDescription}
  url={`${siteUrl}${localizedPath("/tarot/cups")}`}
  image={ogImage}
  schema={schemaGraph}
  keywords={keywords}   // ← use the variable you defined above
/>


      {/* Ambient auras */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-1/3 left-1/2 -translate-x-1/2 w-[1200px] h-[1200px] rounded-full opacity-25 blur-[160px] bg-[radial-gradient(circle,rgba(59,198,214,0.55)_0%,transparent_70%)]" />
        <div className="absolute -bottom-[25%] right-[5%] w-[700px] h-[700px] rounded-full opacity-20 blur-[140px] bg-[radial-gradient(circle,rgba(140,59,90,0.55)_0%,transparent_70%)]" />
      </div>

      {/* HERO (centered, matches MajorArcana.js) */}
      <section className={`pt-6 ${WRAP} ${MAXW}`}>
        <h1 className="text-xl sm:text-2xl tracking-wide text-center font-bold">{isSpanish ? "Copas en el Tarot" : "Cups Tarot Cards"}</h1>
        <div className="flex items-center justify-center mb-1">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
          <span className="px-4 text-[#f3eaff]/100">✦</span>
          <div className="flex-1 h-px bg-gradient-to-r from-transparent via-[#cbb7ea]/70 to-transparent"></div>
        </div>
        <p className={`${TEXT} text-base mt-2`}>{intro}</p>
        <p className={`${TEXT} text-[15px] md:text-[16px] leading-relaxed mt-2`}>{heroExplainer}</p>
      </section>

      {/* SINGLE SUIT PANEL */}
      <section className={`mt-6 ${WRAP} ${MAXW}`}>
        <div className={`${PANEL_BASE} ${SILK_TURQ} p-3 sm:p-6 md:p-6`}>
          <div className="relative z-[1]">
            <h2 className="text-base sm:text-lg font-semibold uppercase tracking-[0.12em] text-white/95">{translateSuitName("Cups").toUpperCase()}</h2>
            <p className={`${TEXT} text-sm leading-relaxed mt-2`}>
              {isSpanish
                ? "Las cartas siguientes muestran el palo completo. Abre cualquier carta para estudiar significado, símbolos y guía para relaciones, sanación y crecimiento personal."
                : "The cards below present the full suit. Open any card to study upright and reversed meanings, symbolism and guidance for relationships, healing and personal development."}
            </p>

            <div className="mt-5 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 sm:gap-4">
              {cupsCards.map((card) => (
                <Link
                  key={card.slug}
                  to={localizedPath(cardPath(card))}
                  className="group rounded-xl border border-white/15 bg-white/5 hover:bg-white/10 hover:border-white/25 hover:-translate-y-1 transition-transform duration-100"
                >
                  <div className="p-2 flex flex-col items-center gap-2">
                    {/* Card image on top, fully visible (no crop) */}
                    <div className="w-full aspect-[2/3] rounded-lg bg-white/5 border border-white/10 overflow-hidden">
                      <img
                        src={card.image || `/card/${card.slug}.webp`}
                        alt={translateCardName(card.name)}
                        className="w-full h-full object-contain select-none pointer-events-none"
                        draggable={false}
                        onContextMenu={(e) => e.preventDefault()}
                      />
                    </div>

                    {/* Title + subtitle below the card */}
                    <div className="text-center">
                      <h3 className="text-sm font-semibold text-white/95">{translateCardName(card.name)}</h3>
                      {card.subtitle ? (
                        <p className="text-xs italic text-white/75">{translateKeywordPhrase(card.subtitle)}</p>
                      ) : null}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
          <div className="absolute inset-x-0 top-0 h-px opacity-60 bg-gradient-to-r from-transparent via-white/40 to-transparent" />
        </div>
      </section>

      {/* FAQ (always open, colored Q/A) */}
<section id="faq" className={`mt-12 mb-16 ${WRAP} ${MAXW}`}>
  <div className="text-center">
    <h2 className="text-base sm:text-lg font-medium">{isSpanish ? COMMON_COPY_ES.faq : "Frequently Asked Questions"}</h2>
  </div>

  <div className="mt-6 rounded-sm border border-white/35 bg-white/5">
    <div className="divide-y divide-white/20">
      {faq.map(({ q, a }, i) => (
        <div key={i} className="px-4 md:px-6 py-4">
          {/* Question */}
<p className="font-semibold text-sm sm:text-base leading-relaxed px-3 py-2 rounded-md bg-white/10 text-white">
  {q}
</p>

{/* Answer */}
<p className="mt-2 text-white/90 text-sm sm:text-base leading-relaxed px-3">
  {a}
</p>

        </div>
      ))}
    </div>
  </div>

  {/* Explore other suits */}
  <div className="text-center mt-10">
    <div className="tf-outline-button-row">
      <Link
        to={localizedPath("/tarot/major-arcana")}
        className="tf-outline-button"
      >
        {translateSuitName("Major Arcana")}
      </Link>
      <Link
        to={localizedPath("/tarot/wands")}
        className="tf-outline-button"
      >
        {translateSuitName("Wands")}
      </Link>
      <Link
        to={localizedPath("/tarot/swords")}
        className="tf-outline-button"
      >
        {translateSuitName("Swords")}
      </Link>
      <Link
        to={localizedPath("/tarot/pentacles")}
        className="tf-outline-button"
      >
        {translateSuitName("Pentacles")}
      </Link>
      <Link
        to={localizedPath("/tarot-cards")}
        className="tf-outline-button"
      >
        {isSpanish ? COMMON_COPY_ES.allCards : "All Cards"}
      </Link>
    </div>
  </div>
</section>

    </div>
  );
}

